package com.devi3east.bashbuddy.dashboard.data.api

import com.devi3east.bashbuddy.dashboard.responses.DashboardResponse
import com.devi3east.bashbuddy.dashboard.responses.PrizeListResponse
import com.devi3east.bashbuddy.dashboard.results.DeleteResult
import com.devi3east.bashbuddy.features.events.requests.CreateDraftTicketRequest
import com.devi3east.bashbuddy.features.events.requests.PauseTicketRequest
import com.devi3east.bashbuddy.features.events.requests.UpdateDraftEventRequest
import com.devi3east.bashbuddy.features.events.requests.UpdateTicketRequest
import com.devi3east.bashbuddy.prizeList.requests.CreatePrizeRequest
import com.devi3east.bashbuddy.prizeList.requests.CreatePrizeSeriesRequest
import com.devi3east.bashbuddy.prizeList.requests.UpdatePrizeRequest
import com.devi3east.bashbuddy.purchase.domain.models.Allocation
import com.devi3east.bashbuddy.purchase.requests.UpdateAllocationsRequest
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.ClientRequestException
import io.ktor.client.plugins.ServerResponseException
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.headers
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.isSuccess

class DashboardApi(
    private val client: HttpClient,
    private val baseUrl: String
) {

    suspend fun getDashboard(token: String): DashboardResponse {
        val response = client.get("$baseUrl/dashboard") {
            headers { append(HttpHeaders.Authorization, "Bearer $token") }
            contentType(ContentType.Application.Json)
        }
        return response.body()
    }

    suspend fun createDraftEvent(token: String): String? {
        val response = client.post("$baseUrl/events/draft") {
            headers { append(HttpHeaders.Authorization, "Bearer $token") }
        }
        return if (response.status.isSuccess()) {
            val body = response.body<Map<String, String>>()
            body["eventId"]
        } else null
    }

    suspend fun updateEvent(token: String, eventId: String, request: UpdateDraftEventRequest): Boolean {
        val response = client.put("$baseUrl/events/update/$eventId") {
            headers { append(HttpHeaders.Authorization, "Bearer $token") }
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        return response.status.isSuccess()
    }

    suspend fun createDraftTicket(token: String, eventId: String, request: CreateDraftTicketRequest): String? {
        val response = client.post("$baseUrl/tickets/draft/$eventId") {
            headers { append(HttpHeaders.Authorization, "Bearer $token") }
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        return if (response.status.isSuccess()) {
            val body = response.body<Map<String, String>>()
            val ticketId = body["ticketId"]
            ticketId
        } else {
            null
        }
    }

    suspend fun updateTicket(token: String, ticketId: String, request: UpdateTicketRequest): Boolean {
        val response = client.put("$baseUrl/tickets/update/$ticketId") {
            headers { append(HttpHeaders.Authorization, "Bearer $token") }
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        return response.status.isSuccess()
    }

    suspend fun deleteEvent(token: String, eventId: String): DeleteResult {
        return try {
            val response: HttpResponse = client.delete("$baseUrl/events/delete/$eventId") {
                headers { append(HttpHeaders.Authorization, "Bearer $token") }
            }
            val result: DeleteResult = response.body()
            result
        } catch (e: ClientRequestException) {
            val result: DeleteResult = e.response.body()
            result
        } catch (e: ServerResponseException) {
            DeleteResult(false, "Server error ${e.response.status.value}")
        } catch (e: Exception) {
            DeleteResult(false, e.message ?: "Unexpected error")
        }
    }

    suspend fun deleteTicket(token: String, ticketId: String): DeleteResult {
        return try {
            val response: HttpResponse = client.delete("$baseUrl/tickets/delete/$ticketId") {
                headers { append(HttpHeaders.Authorization, "Bearer $token") }
            }
            val result: DeleteResult = response.body()
            result
        } catch (e: ClientRequestException) {
            val result: DeleteResult = e.response.body()
            result
        } catch (e: ServerResponseException) {
            DeleteResult(false, "Server error ${e.response.status.value}")
        } catch (e: Exception) {
            DeleteResult(false, e.message ?: "Unexpected error")
        }
    }

    suspend fun adjustPrintedTicketStock(token: String, ticketId: String, newMaxQuantity: Int): Boolean {
        val response = client.put("$baseUrl/tickets/adjust/$ticketId") {
            headers { append(HttpHeaders.Authorization, "Bearer $token") }
            contentType(ContentType.Application.Json)
            setBody(mapOf("newMaxQuantity" to newMaxQuantity))
        }
        return response.status.isSuccess()
    }

    suspend fun getAllocationsForTicket(token: String, ticketId: String): List<Allocation> {
        val response = client.get("$baseUrl/dashboard/allocations/$ticketId") {
            headers { append(HttpHeaders.Authorization, "Bearer $token") }
            contentType(ContentType.Application.Json)
        }
        return if (response.status.isSuccess()) {
            response.body()
        } else emptyList()
    }

    suspend fun updateAllocationChanceNumbers(
        token: String,
        ticketId: String,
        updates: Map<String, List<String>>
    ): Boolean {
        val request = UpdateAllocationsRequest(ticketId, updates)
        val response = client.put("$baseUrl/dashboard/allocations/update") {
            headers { append(HttpHeaders.Authorization, "Bearer $token") }
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        return response.status.isSuccess()
    }

    suspend fun publishEvent(token: String, eventId: String): Boolean {
        val response = client.post("$baseUrl/events/publish/$eventId") {
            headers { append(HttpHeaders.Authorization, "Bearer $token") }
            contentType(ContentType.Application.Json)
        }
        return response.status.isSuccess()
    }

    suspend fun getAllocationsForTicketDetail(token: String, ticketId: String): List<Allocation> {
        val response = client.get("$baseUrl/dashboard/ticket/$ticketId/allocations") {
            headers { append(HttpHeaders.Authorization, "Bearer $token") }
            contentType(ContentType.Application.Json)
        }
        if (response.status.isSuccess()) {
            val allocations = response.body<List<Allocation>>()
            return allocations
        } else {
            response.bodyAsText()
            throw Exception("Failed to fetch allocations: ${response.status}")
        }
    }

    suspend fun createPrize(token: String, eventId: String, request: CreatePrizeRequest): String? {
        val response = client.post("$baseUrl/dashboard/prizes/create/$eventId") {
            headers { append(HttpHeaders.Authorization, "Bearer $token") }
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        return if (response.status.isSuccess()) {
            try {
                val body = response.body<Map<String, String>>()
                val prizeId = body["prizeId"]
                prizeId
            } catch (e: Exception) {
                null
            }
        } else {
            null
        }
    }

    suspend fun createPrizeSeries(
        token: String,
        eventId: String,
        request: CreatePrizeSeriesRequest
    ): List<String>? {
        val response = client.post("$baseUrl/dashboard/prizes/create-series/$eventId") {
            headers { append(HttpHeaders.Authorization, "Bearer $token") }
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        return if (response.status.isSuccess()) {
            try {
                val body = response.body<Map<String, List<String>>>()
                val prizeIds = body["prizeIds"]
                prizeIds
            } catch (e: Exception) {
                null
            }
        } else {
            null
        }
    }

    suspend fun updatePrize(token: String, prizeId: String, request: UpdatePrizeRequest): Boolean {
        val response = client.put("$baseUrl/dashboard/prizes/update/$prizeId") {
            headers { append(HttpHeaders.Authorization, "Bearer $token") }
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        return response.status.isSuccess()
    }

    suspend fun deletePrize(token: String, prizeId: String): DeleteResult {
        return try {
            val response: HttpResponse = client.delete("$baseUrl/dashboard/prizes/delete/$prizeId") {
                headers { append(HttpHeaders.Authorization, "Bearer $token") }
            }
            val result: DeleteResult = response.body()
            result
        } catch (e: ClientRequestException) {
            val result: DeleteResult = e.response.body()
            result
        } catch (e: ServerResponseException) {
            DeleteResult(false, "Server error ${e.response.status.value}")
        } catch (e: Exception) {
            DeleteResult(false, e.message ?: "Unexpected error")
        }
    }

    suspend fun getPrizesByEventId(eventId: String): PrizeListResponse {
        val response = client.get("$baseUrl/events/prizes/$eventId") {
            contentType(ContentType.Application.Json)
        }
        return response.body()
    }

    suspend fun pauseTicket(token: String, ticketId: String, paused: Boolean): Boolean {
        return try {
            val response: HttpResponse = client.patch("$baseUrl/tickets/pause/$ticketId") {
                headers {
                    append(HttpHeaders.Authorization, "Bearer $token")
                }
                contentType(ContentType.Application.Json)
                setBody(PauseTicketRequest(paused = paused))
            }

            response.status.isSuccess()
        } catch (e: Exception) {
            false
        }
    }

}